import { useState } from "react";
import { format } from "date-fns";
import { friendlyFormatIBAN } from "ibantools";
import { AlertCircle, AlertTriangle, CheckCircle, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { BookingFormData } from "@/shared/types";
import type { PaymentSettings } from "@/backend/services/paymentSettings";

interface BookingSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  bookingReference: string;
  bookingData: BookingFormData;
  residenceName?: string;
  roomTypeName?: string;
  amount: number | null;
  paymentSettings: PaymentSettings | undefined;
}

const formatEuro = (value: number) =>
  new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

const renderInstructions = (
  template: string,
  amount: number | null,
  reference: string,
) =>
  template
    .replace(/\{amount\}/gi, amount != null ? formatEuro(amount) : "")
    .replace(/\{reference\}/gi, reference);

export const BookingSuccessDialog = ({
  open,
  onClose,
  bookingReference,
  bookingData,
  residenceName,
  roomTypeName,
  amount,
  paymentSettings,
}: BookingSuccessDialogProps) => {
  const [copied, setCopied] = useState(false);
  const settingsReady = !!paymentSettings && paymentSettings.iban.trim() !== "";
  const formattedIban = settingsReady ? friendlyFormatIBAN(paymentSettings!.iban) ?? paymentSettings!.iban : "";
  const instructions = settingsReady
    ? renderInstructions(paymentSettings!.paymentInstructions, amount, bookingReference)
    : "";
  const firstPaymentLabel = paymentSettings?.firstPaymentLabel?.trim() || "First month's rent";

  const handleCopyReference = async () => {
    try {
      await navigator.clipboard.writeText(bookingReference);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-7 h-7 text-green-600" />
          </div>
          <DialogTitle className="text-center text-xl">Booking request received</DialogTitle>
          <DialogDescription className="text-center">
            Your booking is reserved as <span className="font-medium">pending</span>. To confirm it, please
            transfer the {firstPaymentLabel.toLowerCase()} using the details below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!settingsReady ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold mb-1">Payment details not yet configured</p>
              <p>
                We've received your request. Our team will contact you with payment instructions shortly.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
              {amount != null && (
                <div className="text-center pb-2 border-b border-primary/20">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{firstPaymentLabel}</p>
                  <p className="text-3xl font-bold text-primary">{formatEuro(amount)}</p>
                </div>
              )}

              <div className="grid grid-cols-[7rem_1fr] gap-y-2 text-sm">
                <span className="text-muted-foreground">IBAN</span>
                <span className="font-mono font-semibold break-all">{formattedIban}</span>

                <span className="text-muted-foreground">Account holder</span>
                <span className="font-medium">{paymentSettings!.accountHolder}</span>

                <span className="text-muted-foreground">Bank</span>
                <span className="font-medium">{paymentSettings!.bankName}</span>

                <span className="text-muted-foreground self-center">Reference</span>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-semibold break-all">{bookingReference}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={handleCopyReference}
                    title="Copy reference"
                    aria-label="Copy reference"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground italic">
                {copied ? "Reference copied." : "Use this reference in the transfer description so we can match your payment."}
              </p>

              {instructions && (
                <div className="pt-2 border-t border-primary/20">
                  <p className="text-sm text-foreground whitespace-pre-line">{instructions}</p>
                </div>
              )}
            </div>
          )}

          <details className="rounded-lg bg-muted/40 px-3 py-2 text-sm">
            <summary className="cursor-pointer text-muted-foreground select-none">
              Booking summary
            </summary>
            <div className="mt-2 space-y-1.5 text-foreground">
              <p>
                <span className="text-muted-foreground">Residence: </span>
                <span className="font-medium">{residenceName || "N/A"}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Room: </span>
                <span className="font-medium">{roomTypeName || "N/A"}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Dates: </span>
                <span className="font-medium">
                  {bookingData.checkIn && format(bookingData.checkIn, "PPP")} –{" "}
                  {bookingData.checkOut && format(bookingData.checkOut, "PPP")}
                </span>
              </p>
              <p>
                <span className="text-muted-foreground">Guest: </span>
                <span className="font-medium">
                  {bookingData.guests[0]?.name} · {bookingData.guests[0]?.email}
                </span>
              </p>
              {bookingData.notes && (
                <p>
                  <span className="text-muted-foreground">Notes: </span>
                  <span className="font-medium">{bookingData.notes}</span>
                </p>
              )}
            </div>
          </details>

          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p>
              Your booking will be confirmed only after we receive the transfer. Save your reference in case
              you close this page, we've also emailed these details to you.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="w-full" variant="coral">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface BookingErrorDialogProps {
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
  errorMessage?: string;
}

export const BookingErrorDialog = ({
  open,
  onClose,
  onRetry,
  errorMessage = "There was an error submitting your booking. This might be due to a network issue or a temporary problem with our servers.",
}: BookingErrorDialogProps) => {
  const handleRetry = () => {
    onClose();
    onRetry();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <DialogTitle className="text-center text-2xl">Booking Failed</DialogTitle>
          <DialogDescription className="text-center">
            {errorMessage}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-amber-50 rounded-lg p-4 text-sm">
            <p className="text-amber-900">
              <strong>Please try again.</strong> If the problem persists, please contact our
              support team for assistance.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button onClick={handleCancel} variant="outline" className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleRetry} variant="coral" className="flex-1">
            Try Again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
